import React from "react";
import { typography, borders, textColors, surfaces, interactive } from "../../../design-system/tokens";
import { transformColor } from "../../../core/utils/generalUtils";
import InfoIcon from "../InfoIcon";

const DEFAULT_CHOICES = [
  { value: "none", label: "None" },
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "top-right", label: "Top Right" },
  { value: "top-left", label: "Top Left" },
  { value: "bottom-right", label: "Bottom Right" },
  { value: "bottom-left", label: "Bottom Left" },
];

function orderChoicesForGrid(choices) {
  // Arrange like a 3x3 directional pad with "None" in the center.
  const preferredOrder = [
    "top-left",
    "top",
    "top-right",
    "left",
    "none",
    "right",
    "bottom-left",
    "bottom",
    "bottom-right",
  ];
  const byValue = new Map((choices || []).map((c) => [c.value, c]));
  const ordered = preferredOrder.map((v) => byValue.get(v)).filter(Boolean);
  // Append any unknown/custom values at the end (stable).
  const known = new Set(preferredOrder);
  const extras = (choices || []).filter((c) => c?.value && !known.has(c.value));
  return [...ordered, ...extras];
}

function getTextColor(allValues) {
  let raw =
    allValues?.text_color ||
    allValues?.textColor ||
    allValues?.character_style?.text_color ||
    allValues?.characterStyle?.text_color ||
    "#111827";
  if (typeof raw === "string") {
    // Be forgiving with legacy/prefixed tokens (e.g. "text-blue-600").
    raw = raw.trim().replace(/^(text|bg|border)-/, "");
  }
  // Convert tailwind tokens to hex so we can safely create rgba.
  return transformColor(raw, 100, "hex");
}

function getCopyColor(allValues, textHex) {
  const raw = allValues?.text_offset_copy_color;
  if (raw) {
    // Explicit color: respect it fully.
    return transformColor(raw, 100, "rgba");
  }
  // Default: subtle version of text color.
  return transformColor(textHex, 35, "rgba");
}

function hexToRgbChannels(hexColor) {
  if (typeof hexColor !== "string") return null;
  const normalized = hexColor.trim().replace(/^#/, "");
  if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(normalized)) return null;

  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((channel) => `${channel}${channel}`)
          .join("")
      : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

function getRelativeLuminance(channel) {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

function getContrastRatio(foregroundHex, backgroundHex) {
  const foreground = hexToRgbChannels(foregroundHex);
  const background = hexToRgbChannels(backgroundHex);
  if (!foreground || !background) return Number.POSITIVE_INFINITY;

  const foregroundLuminance =
    0.2126 * getRelativeLuminance(foreground.r) +
    0.7152 * getRelativeLuminance(foreground.g) +
    0.0722 * getRelativeLuminance(foreground.b);
  const backgroundLuminance =
    0.2126 * getRelativeLuminance(background.r) +
    0.7152 * getRelativeLuminance(background.g) +
    0.0722 * getRelativeLuminance(background.b);

  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);
  return (lighter + 0.05) / (darker + 0.05);
}

function needsDarkPreviewSurface(textHex) {
  // If the selected text color barely contrasts with a light panel surface,
  // swap the preview tile to a dark neutral so the glyph remains visible.
  return getContrastRatio(textHex, "#ffffff") < 2.5;
}

function OffsetPreview({
  direction,
  textHex,
  copyRgba,
  isSelected,
}) {
  // IMPORTANT UX: this is a static preview for choosing direction only.
  // It must NOT change based on current "Offset Distance", "Offset Size", or
  // "Connect Offset" values — those controls have their own fields.
  const offset = 7; // px
  const s = 1;
  const map = {
    // NOTE: this preview intentionally inverts the offset direction so that
    // "bottom" visually shows the copy ABOVE the main text (and so forth),
    // matching the editor's semantics.
    right: [-offset, 0],
    left: [offset, 0],
    top: [0, offset],
    bottom: [0, -offset],
    "top-right": [-offset, offset],
    "top-left": [offset, offset],
    "bottom-right": [-offset, -offset],
    "bottom-left": [offset, -offset],
  };
  const [dx, dy] = map[direction] || [offset, offset];
  const shouldUseDarkPreview = needsDarkPreviewSurface(textHex);
  const previewSurfaceClasses = shouldUseDarkPreview
    ? isSelected
      ? "bg-gray-800 dark:bg-dark-700 ring-1 ring-inset ring-white/30"
      : "bg-gray-800 dark:bg-dark-700 ring-1 ring-inset ring-white/10"
    : isSelected
      ? "!bg-blue-50"
      : "!bg-white";

  return (
    <div
      className={`relative flex items-center justify-center h-11 w-11 rounded-lg transition-colors ${previewSurfaceClasses}`}
    >
      {direction !== "none" ? (
        <span
          aria-hidden="true"
          className="absolute text-2xl leading-none select-none pointer-events-none font-semibold"
          style={{
            color: copyRgba,
            transform: `translate(${dx}px, ${dy}px) scale(${s})`,
            transformOrigin: "top center",
          }}
        >
          A
        </span>
      ) : null}
      <span
        className="relative text-2xl leading-none select-none font-semibold"
        style={{ color: textHex }}
      >
        {direction === "none" ? "✕" : "A"}
      </span>
    </div>
  );
}

function OffsetChip({
  opt,
  selected,
  onSelect,
  textHex,
  copyRgba,
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(opt.value)}
      className={`flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2 w-full transition-all ${
        selected
          ? `${interactive.selectedBg} text-white shadow-sm`
          : `${surfaces.primary} ${surfaces.hover}`
      }`}
      aria-pressed={selected}
    >
      <OffsetPreview
        direction={opt.value}
        textHex={textHex}
        copyRgba={copyRgba}
        isSelected={selected}
      />
      <div className={`text-center ${typography.caption} ${selected ? "text-white/80" : textColors.tertiary}`}>
        {opt.label}
      </div>
    </button>
  );
}

export default function TextOffsetCopyField({ label, help, value, onChange, choices, allValues, infoText }) {
  const normalizedRaw = Array.isArray(choices) && choices.length ? choices : DEFAULT_CHOICES;
  const normalized = orderChoicesForGrid(normalizedRaw);
  const textHex = getTextColor(allValues);
  const copyRgba = getCopyColor(allValues, textHex);

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
        </label>
      )}
      <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-1.5`}>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          {normalized.map((opt) => (
            <OffsetChip
              key={opt.value}
              opt={opt}
              selected={(value || "none") === opt.value}
              onSelect={(val) => onChange(val)}
              textHex={textHex}
              copyRgba={copyRgba}
            />
          ))}
        </div>
      </div>
      {help && <div className={`mt-1.5 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
}
